import { useState, useCallback, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../core/store';
import { ErrorSource } from '../../../core/constant/model/api/api';
import { getValidationSchemaForStep, getValidationSchemaForStep2 } from '../utils/enrollmentSchema';
import { ENROLLMENT_FORM_STEPS, EnrollmentSteps } from '../../../core/constant/constant';
import { useUpdateEnrollmentMutation } from '../hooks/enrollmentApi';
import { EnrollmentFormData } from '../types/enrollment';
import { UnknownAction, Dispatch } from 'redux';

interface EnrollmentFormState {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  methods: UseFormReturn<EnrollmentFormData>;
  getProgressValue: () => number;
}

interface ApiErrorState {
  apiErrors: Record<string, any>;
  hasApiError: (source: ErrorSource) => boolean;
  getApiErrorBySource: (source: ErrorSource) => any;
}

interface SubmissionState {
  submitStepData: (
    stepNumber: number,
    formData: EnrollmentFormData,
    updateReduxFn: (data: EnrollmentFormData, dispatch: Dispatch<UnknownAction>, userId: string) => void,
    prepareApiDataFn: (data: EnrollmentFormData, userId: string) => any
  ) => Promise<{ success: boolean; error?: any }>;
  isLoading: boolean;
}

export const useEnrollmentForm = (initialStep = 0): EnrollmentFormState => {
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const memberSubscription = useSelector((state: RootState) => state.memberSubscription)
  const enrollmentStep = useSelector((state: RootState) => state.enrollment.enrollmentStep);
  const isEnrollmentComplete = [EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION, EnrollmentSteps.ENROLLMENT_AVAILABILITY, EnrollmentSteps.ENROLLMENT_EMERGENCY_CONTACT];
 
 
  const currentSchema = useMemo(
    () => {
      if (isEnrollmentComplete.includes(enrollmentStep)) {
        return getValidationSchemaForStep2(activeStep, personalDetails?.dob)
      } else {
        return getValidationSchemaForStep(activeStep, personalDetails?.dob)
      }
    },
    [activeStep, enrollmentStep]
  );


  const methods = useForm<EnrollmentFormData>({
    resolver: yupResolver(currentSchema),
    mode: 'onTouched',
    shouldFocusError: true,
    shouldUseNativeValidation: false,
    defaultValues: {
      isStudent: false,
      school: '',
    }
  });

  const getProgressValue = useCallback(
    (): number => ((activeStep + 1) / ENROLLMENT_FORM_STEPS.length) * 100,
    [activeStep]
  );

  return {
    activeStep,
    setActiveStep,
    isSaving,
    setIsSaving,
    methods,
    getProgressValue,
  };
};

export const useApiErrors = (): ApiErrorState => {
  const apiErrors = useSelector((state: RootState) => state.apiError?.errors || {});

  const hasApiError = useCallback(
    (source: ErrorSource): boolean => !!apiErrors[source],
    [apiErrors]
  );

  const getApiErrorBySource = useCallback(
    (source: ErrorSource): any => apiErrors[source],
    [apiErrors]
  );

  return {
    apiErrors,
    hasApiError,
    getApiErrorBySource,
  };
};

export const useEnrollmentSubmission = (userId: string): SubmissionState => {
  const dispatch = useDispatch();
  const updateEnrollmentMutation = useUpdateEnrollmentMutation();

  const submitStepData = useCallback(
    async (
      stepNumber: number,
      formData: EnrollmentFormData,
      updateReduxFn: (data: EnrollmentFormData, dispatch: Dispatch<UnknownAction>, userId: string) => void,
      prepareApiDataFn: (data: EnrollmentFormData, userId: string) => any
    ): Promise<{ success: boolean; error?: any }> => {
      try {

        if (updateReduxFn) {
          updateReduxFn(formData, dispatch, userId);
        }

        if (prepareApiDataFn) {
          const apiData = prepareApiDataFn(formData, userId);
          await updateEnrollmentMutation.mutateAsync(apiData);
        }
        return { success: true };
      } catch (error) {
        console.error(`Error submitting step ${stepNumber} data:`, error);
        throw error;
      }
    },
    [dispatch, userId, updateEnrollmentMutation]
  );

  return {
    submitStepData,
    isLoading: updateEnrollmentMutation.isLoading,
  };
};